/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import config from "../../../../share/config";
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: "start",
  INPUT_DATA: "inputData",
  STOP: "stop",
  FAILED: "failed",
  UNDEFINED_ERROR: "undefinedError",
  ABNORMALITY_DISPLAY: "abnormalityDisplay"
};

/** バリデーション値 */
const VALIDATE = {
  APPLIED_MODEL: 7,
  OP_CODE: 4
};

export default {
  name: "requirement116",
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      appliedModel: "",
      optionCode: "",
      validateAppliedModel: undefined,
      validateOptionCode: undefined,
      modal_requirement_116: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.UNDEFINED_ERROR:
        case PHASE.ABNORMALITY_DISPLAY:
          return "danger";
        case PHASE.STOP:
          return "success";
        case PHASE.INPUT_DATA:
        default:
          return "secondary";
      }
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /**
     * バリデーションチェック(アプライドモデル)
     * @returns 確認結果 (true: OK, false: NG)
     */
    checkValidateAppliedModel() {
      this.validateAppliedModel = undefined;

      // 空の場合エラー
      if (!this.appliedModel) {
        this.validateAppliedModel = { valid: true, msg: this.$t(`message.alert_validate_required`) };
        return false;
      }
      // 不正文字列が含まれている場合はエラー
      if (this.checkInvalidCharacter(this.appliedModel)) {
        this.validateAppliedModel = { valid: true, msg: this.$t(`message.alert_validate_invalid_pattern`) };
        return false;
      }
      // 桁数が7桁でない場合はエラー
      if (this.appliedModel.length !== VALIDATE.APPLIED_MODEL) {
        this.validateAppliedModel = { valid: true, msg: this.$t(`message.alert_validate_max_length`, ['7']) };
        return false;
      }
      // OK判定
      return true;
    },

    /**
     * バリデーションチェック(OPコード)
     * @returns 確認結果 (true: OK, false: NG)
     */
    checkValidateOptionCode() {
      this.validateOptionCode = undefined;
      // 不正文字列が含まれている場合はエラー
      if (this.checkInvalidCharacter(this.optionCode)) {
        this.validateOptionCode = { valid: true, msg: this.$t(`message.alert_validate_invalid_pattern`) };
        return false;
      }
      // 4桁ではない、かつ、空白でない場合はエラー
      if (this.optionCode.replace(" ", "").length !== VALIDATE.OP_CODE && this.optionCode !== "") {
        this.validateOptionCode = { valid: true, msg: this.$t(`message.alert_validate_max_length`, ['4']) };
        return false;
      }
      // OK判定
      return true;
    },

    /**
     * 使用不可な文字列が含まれていないかの確認関数
     * @param code 対象code
     */
    checkInvalidCharacter(code) {
      // 半角英数字以外の場合は弾く
      const halfReqExp = new RegExp(/^[A-Za-z0-9]*$/);
      return !halfReqExp.test(code);
    },

    /** 初期化処理 */
    init() {
      this.modal_requirement_116 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_116 = false;
    },

    /** バリアントデータ取得 */
    async getValiantData() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: 'check'
        };

        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // responseが存在しない場合、共通エラーに遷移
        if (!response) {
          this.showUnexpectedError();
          return;
        }

        switch (response.data.status) {
          case config.STATUS_WSP_COMPLETED:
            this.phase = PHASE.INPUT_DATA;
            break;
          case config.STATUS_WSP_FAILED:
          default:
            // 異常終了
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** アプライドモデル・オプションコード入力 */
    async inputData() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: 'match_input',
          applied_model: this.appliedModel,
          option_code: this.optionCode
        };

        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // responseが存在しない場合、共通エラーに遷移
        if (!response) {
          this.showUnexpectedError();
          return;
        }

        switch (response.data.status) {
          case config.STATUS_WSP_COMPLETED:
            //有効なアプライドモデルとオプションコード
            await this.variantDataJudgement();
            break;
          case config.STATUS_WSP_FAILED:
            switch (response.data.async_code) {
              case error.WSP_116_APPLIED_UNDEFINED:
                if (this.optionCode !== '') {
                  // アプライドモデル未定義で、オプションコードが入力されている場合は、オプションコードを空にして再度実行する
                  this.optionCode = '';
                  // オプションコードを空に設定して再実行
                  await this.inputData();
                  return;
                }
                // アプライドコード未定義
                this.phase = PHASE.UNDEFINED_ERROR;
                break;
              default:
                this.showUnexpectedError();
                break;
            }
            break;
          default:
            // 異常終了
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** バリアントデータ判定 */
    async variantDataJudgement() {
      try {
        this.isLoading = true;
        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: 'match_romid',
          applied_model: this.appliedModel,
          option_code: this.optionCode
        };

        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // responseが存在しない場合、共通エラーに遷移
        if (!response) {
          this.showUnexpectedError();
          return;
        }

        switch (response.data.status) {
          case config.STATUS_WSP_COMPLETED:
            // 正常終了
            this.phase = PHASE.STOP;
            break;
          case config.STATUS_WSP_FAILED:
            switch (response.data.async_code) {
              case error.WSP_116_APPLIED_FAILED:
                this.phase = PHASE.ABNORMALITY_DISPLAY;
                break;
              default:
                this.showUnexpectedError();
                break;
            }
            break;
          default:
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了時の処理 */
    exit() {
      this.closeWizard();
    },

    /** 入力画面 OK押下時の処理 */
    onInputOkClicked() {
      // バリデーションチェックしてOKのときのみ実行する
      let result = this.checkValidateAppliedModel();
      result = result && this.checkValidateOptionCode();
      if (result) {
        this.inputData();
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    }
  },

  async mounted() {
    this.init();
    this.getValiantData();
  }
};
